// frontend/src/contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api'; // Importa nossa instância configurada do Axios

// 1. Cria o Contexto
const AuthContext = createContext(null);

// 2. Cria o Provedor (Provider Component)
export const AuthProvider = ({ children }) => {
    // --- Estados do Contexto ---
    const [user, setUser] = useState(null); // Armazena dados do usuário logado (id, email)
    const [token, setToken] = useState(null); // Armazena o token JWT
    const [isAuthenticated, setIsAuthenticated] = useState(false); // Flag de autenticação
    const [isLoading, setIsLoading] = useState(true); // Flag para indicar carregamento inicial

    // --- Função para carregar usuário do localStorage na inicialização ---
    const loadUserFromStorage = useCallback(() => {
        console.log("AuthContext: Tentando carregar usuário do localStorage...");
        setIsLoading(true); // Começa a carregar
        try {
            const storedUserString = localStorage.getItem('user');
            if (storedUserString) {
                const storedUser = JSON.parse(storedUserString);
                if (storedUser && storedUser.token) {
                    console.log("AuthContext: Usuário encontrado no localStorage", storedUser);
                    // Define o estado interno
                    setUser({ _id: storedUser._id, email: storedUser.email });
                    setToken(storedUser.token);
                    setIsAuthenticated(true);
                    // **IMPORTANTE:** Define o header padrão do Axios para futuras requisições
                    api.defaults.headers.common['Authorization'] = `Bearer ${storedUser.token}`;
                } else {
                    console.log("AuthContext: Dados inválidos no localStorage.");
                    localStorage.removeItem('user'); // Limpa dados inválidos
                    // Garante que o header do Axios seja removido se os dados forem inválidos
                     delete api.defaults.headers.common['Authorization'];
                }
            } else {
                 console.log("AuthContext: Nenhum usuário no localStorage.");
                  // Garante que o header do Axios seja removido se não houver usuário
                 delete api.defaults.headers.common['Authorization'];
            }
        } catch (error) {
            console.error("AuthContext: Erro ao processar localStorage", error);
            localStorage.removeItem('user'); // Limpa em caso de erro de parse
            setIsAuthenticated(false);
            setUser(null);
            setToken(null);
            delete api.defaults.headers.common['Authorization'];
        } finally {
            setIsLoading(false); // Termina o carregamento
            console.log("AuthContext: Carregamento inicial finalizado. Autenticado:", isAuthenticated); // Log final do estado
        }
     // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Adicionamos isAuthenticated aqui para o log final refletir o estado atualizado, mas não queremos que isso recarregue o efeito


    // --- Efeito para carregar o usuário na montagem inicial do Provider ---
    useEffect(() => {
        loadUserFromStorage();
    }, [loadUserFromStorage]); // Executa a função de carregamento uma vez

    // --- Função de Login ---
    // Recebe os dados retornados pela API (que incluem _id, email, token)
    const login = useCallback((userData) => {
        console.log("AuthContext: Efetuando login com dados:", userData);
        try {
            // Valida se os dados mínimos existem
            if (!userData || !userData.token || !userData._id || !userData.email) {
                throw new Error("Dados de usuário inválidos recebidos para login.");
            }

            const dataToStore = {
                _id: userData._id,
                email: userData.email,
                token: userData.token
            };

            // 1. Armazena no localStorage (como string JSON)
            localStorage.setItem('user', JSON.stringify(dataToStore));
            // 2. Define o estado interno
            setUser({ _id: dataToStore._id, email: dataToStore.email });
            setToken(dataToStore.token);
            setIsAuthenticated(true);
            // 3. Define o header padrão do Axios IMEDIATAMENTE
            api.defaults.headers.common['Authorization'] = `Bearer ${dataToStore.token}`;
            console.log("AuthContext: Login bem-sucedido. Estado atualizado.");

        } catch (error) {
             console.error("AuthContext: Erro durante o processo de login no contexto:", error);
             // Limpa o estado em caso de erro
             logout(); // Chama logout para limpar tudo
        }
    }, []);

    // --- Função de Logout ---
    const logout = useCallback(() => {
        console.log("AuthContext: Efetuando logout...");
        // 1. Remove do localStorage
        localStorage.removeItem('user');
        // 2. Limpa o estado interno
        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
        // 3. Remove o header padrão do Axios
        delete api.defaults.headers.common['Authorization'];
        console.log("AuthContext: Logout concluído.");
        // O redirecionamento pode ser feito no componente que chama logout
    }, []);

    // --- Valor fornecido pelo Contexto ---
    // Inclui os estados e as funções que os componentes precisarão
    const authContextValue = {
        user,
        token,
        isAuthenticated,
        isLoading,
        login,
        logout,
        // Poderíamos adicionar register aqui se a lógica for diferente do login
    };

    // Retorna o Provider envolvendo os componentes filhos (children)
    // e passando o valor (estados e funções) para eles
    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
        </AuthContext.Provider>
    );
};

// 3. Cria o Hook customizado para usar o Contexto
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth deve ser usado dentro de um AuthProvider');
    }
    return context;
};